import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Users, Target, Calendar, MessageSquare } from 'lucide-react';
import { isCampaignPastEndDate } from '@/lib/campaigns';

interface Campaign {
  id: string;
  title: string;
  description: string;
  goal_slices: number;
  total_slices_raised: number;
  backer_count: number;
  status: string;
  end_date: string | null;
  created_at: string;
  work_id: string | null;
  works: { id: string; title: string } | null;
}

interface Backer {
  id: string;
  user_id: string;
  slices_donated: number;
  backed_at: string | null;
}

interface CampaignUpdate {
  id: string;
  campaign_id: string;
  publisher_id: string;
  title: string;
  body: string;
  created_at: string | null;
}

function daysRemaining(endDate: string | null): string {
  if (!endDate) return '';
  const diff = Math.ceil((new Date(endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return 'Ended';
  if (diff === 0) return 'Ends today';
  return `${diff} day${diff !== 1 ? 's' : ''} remaining`;
}

function fmt(n: number) {
  return n.toLocaleString();
}

export default function CampaignDetail() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { publisher } = useAuth();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [backers, setBackers] = useState<Backer[]>([]);
  const [loading, setLoading] = useState(true);

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Cancel confirm
  const [cancelOpen, setCancelOpen] = useState(false);

  // Campaign updates
  const [updates, setUpdates] = useState<CampaignUpdate[]>([]);
  const [updateTitle, setUpdateTitle] = useState('');
  const [updateBody, setUpdateBody] = useState('');
  const [updatePosting, setUpdatePosting] = useState(false);

  const todayStr = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (campaignId && publisher?.id) fetchCampaign();
  }, [campaignId, publisher?.id]);

  const fetchCampaign = async () => {
    if (!campaignId || !publisher?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('crowdfunding_campaigns')
        .select('*, works(id, title)')
        .eq('id', campaignId)
        .eq('publisher_id', publisher.id)
        .single();
      if (error) throw error;
      let campaignData = data as Campaign;

      if (
        campaignData.status === 'active' &&
        isCampaignPastEndDate(campaignData.end_date)
      ) {
        const { error: closeError } = await supabase
          .from('crowdfunding_campaigns')
          .update({ status: 'completed' })
          .eq('id', campaignData.id);
        if (!closeError) {
          campaignData = { ...campaignData, status: 'completed' };
        }
      }

      setCampaign(campaignData);

      // Fetch backers (table may not exist yet — handle gracefully)
      try {
        const { data: backersData } = await supabase
          .from('campaign_backers')
          .select('id, user_id, slices_donated, backed_at')
          .eq('campaign_id', campaignId)
          .order('backed_at', { ascending: false })
          .limit(10);
        setBackers(backersData || []);
      } catch {
        setBackers([]);
      }

      // Fetch campaign updates (reverse-chronological)
      try {
        const { data: updatesData } = await supabase
          .from('campaign_updates')
          .select('id, campaign_id, publisher_id, title, body, created_at')
          .eq('campaign_id', campaignId)
          .order('created_at', { ascending: false });
        setUpdates(updatesData || []);
      } catch {
        setUpdates([]);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!campaign) return;
    try {
      const { error } = await supabase
        .from('crowdfunding_campaigns')
        .update({ status: 'active' })
        .eq('id', campaign.id);
      if (error) throw error;
      setCampaign(c => c ? { ...c, status: 'active' } : c);
      toast.success('Campaign activated');
    } catch (err: any) {
      toast.error(err.message || 'Failed to activate');
    }
  };

  const handleCancel = async () => {
    if (!campaign) return;
    try {
      const { data, error } = await supabase.rpc('cancel_campaign', { p_campaign_id: campaign.id });
      if (error) throw error;
      const result = data as { error?: string } | null;
      if (result?.error) throw new Error(result.error);
      setCampaign(c => c ? { ...c, status: 'cancelled' } : c);
      toast.success('Campaign cancelled — backers will be refunded');
    } catch (err: any) {
      toast.error(err.message || 'Failed to cancel campaign');
    } finally {
      setCancelOpen(false);
    }
  };

  const handlePostUpdate = async () => {
    if (!campaign || !publisher?.id) return;
    if (!updateTitle.trim()) { toast.error('Title is required'); return; }
    if (!updateBody.trim()) { toast.error('Body is required'); return; }
    setUpdatePosting(true);
    try {
      const { data, error } = await supabase
        .from('campaign_updates')
        .insert({
          campaign_id: campaign.id,
          publisher_id: publisher.id,
          title: updateTitle.trim(),
          body: updateBody.trim(),
        })
        .select('id, campaign_id, publisher_id, title, body, created_at')
        .single();
      if (error) throw error;
      setUpdates(prev => [data as CampaignUpdate, ...prev]);
      setUpdateTitle('');
      setUpdateBody('');
      toast.success('Update posted');
    } catch (err: any) {
      toast.error(err.message || 'Failed to post update');
    } finally {
      setUpdatePosting(false);
    }
  };

  const openEdit = () => {
    if (!campaign) return;
    setEditTitle(campaign.title);
    setEditDescription(campaign.description);
    setEditEndDate(campaign.end_date ? campaign.end_date.split('T')[0] : '');
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!campaign) return;
    if (!editTitle.trim()) { toast.error('Title is required'); return; }
    if (!editEndDate || editEndDate <= todayStr) { toast.error('End date must be in the future'); return; }
    setEditSaving(true);
    try {
      const { error } = await supabase
        .from('crowdfunding_campaigns')
        .update({ title: editTitle.trim(), description: editDescription.trim(), end_date: editEndDate })
        .eq('id', campaign.id);
      if (error) throw error;
      setCampaign(c => c ? { ...c, title: editTitle.trim(), description: editDescription.trim(), end_date: editEndDate } : c);
      toast.success('Campaign updated');
      setEditOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update');
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading campaign...</div>;
  }

  if (!campaign) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Campaign not found.</p>
        <Button className="mt-4" onClick={() => navigate('/campaigns')}>Back to Campaigns</Button>
      </div>
    );
  }

  const pct = campaign.goal_slices > 0
    ? Math.min(100, Math.round((campaign.total_slices_raised / campaign.goal_slices) * 100))
    : 0;

  const isReadOnly = campaign.status === 'completed' || campaign.status === 'cancelled';

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/campaigns')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold">{campaign.title}</h1>
            <Badge
              variant="outline"
              className={
                campaign.status === 'active' ? 'text-green-500 border-green-500/30' :
                campaign.status === 'draft' ? 'text-yellow-500 border-yellow-500/30' :
                campaign.status === 'completed' ? 'text-blue-500 border-blue-500/30' :
                'text-muted-foreground'
              }
            >
              {campaign.status}
            </Badge>
          </div>
          {campaign.works && (
            <p className="text-sm text-muted-foreground mt-1">
              Linked to:{' '}
              <Link to="/content" className="hover:underline text-primary">
                {campaign.works.title}
              </Link>
            </p>
          )}
        </div>
        {!isReadOnly && (
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={openEdit}>Edit</Button>
            {campaign.status === 'draft' && (
              <Button size="sm" onClick={handleActivate}>Activate</Button>
            )}
            {campaign.status === 'active' && (
              <Button size="sm" variant="destructive" onClick={() => setCancelOpen(true)}>Cancel</Button>
            )}
          </div>
        )}
      </div>

      {/* Progress */}
      <Card className="partner-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Funding Progress
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-semibold text-lg">{pct}% funded</span>
              <span className="text-muted-foreground">{fmt(campaign.total_slices_raised)} / {fmt(campaign.goal_slices)} slices</span>
            </div>
            <Progress value={pct} className="h-3" />
          </div>
          <div className="grid grid-cols-3 gap-4 pt-2">
            <div className="text-center">
              <p className="text-2xl font-bold">{fmt(campaign.total_slices_raised)}</p>
              <p className="text-xs text-muted-foreground">Slices raised</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{fmt(campaign.backer_count)}</p>
              <p className="text-xs text-muted-foreground">Backers</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{campaign.end_date ? daysRemaining(campaign.end_date) : '—'}</p>
              <p className="text-xs text-muted-foreground">Time left</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Description */}
      <Card className="partner-card">
        <CardHeader>
          <CardTitle>About This Campaign</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{campaign.description}</p>
        </CardContent>
      </Card>

      {/* Details */}
      <Card className="partner-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Campaign Details
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Created</p>
            <p className="font-medium">{new Date(campaign.created_at).toLocaleDateString()}</p>
          </div>
          <div>
            <p className="text-muted-foreground">End Date</p>
            <p className="font-medium">{campaign.end_date ? new Date(campaign.end_date).toLocaleDateString() : '—'}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Goal</p>
            <p className="font-medium">{fmt(campaign.goal_slices)} slices</p>
          </div>
          <div>
            <p className="text-muted-foreground">Status</p>
            <p className="font-medium capitalize">{campaign.status}</p>
          </div>
        </CardContent>
      </Card>

      {/* Recent Backers */}
      <Card className="partner-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Recent Backers
          </CardTitle>
          <CardDescription>{fmt(campaign.backer_count)} total backer{campaign.backer_count !== 1 ? 's' : ''}</CardDescription>
        </CardHeader>
        <CardContent>
          {backers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No backers yet.</p>
          ) : (
            <div className="space-y-2">
              {backers.map(b => (
                <div key={b.id} className="flex items-center justify-between text-sm py-1.5 border-b border-border/30 last:border-0">
                  <span className="text-muted-foreground font-mono text-xs truncate">{b.user_id.slice(0, 8)}...</span>
                  <span className="font-medium">{fmt(b.slices_donated)} slices</span>
                  {b.backed_at && <span className="text-muted-foreground">{new Date(b.backed_at).toLocaleDateString()}</span>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Campaign Updates */}
      <Card className="partner-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Campaign Updates
          </CardTitle>
          <CardDescription>Keep your backers informed with progress updates</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Post Update form — only for active campaigns */}
          {campaign.status === 'active' && (
            <div className="space-y-3 border border-border/40 rounded-lg p-4 bg-muted/20">
              <p className="text-sm font-medium">Post an Update</p>
              <div className="space-y-2">
                <Label htmlFor="update-title">Title</Label>
                <Input
                  id="update-title"
                  placeholder="e.g. We hit 50% of our goal!"
                  value={updateTitle}
                  onChange={e => setUpdateTitle(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="update-body">Message</Label>
                <Textarea
                  id="update-body"
                  placeholder="Share news, behind-the-scenes progress, or a thank-you to your backers…"
                  value={updateBody}
                  onChange={e => setUpdateBody(e.target.value)}
                  rows={4}
                  className="resize-none"
                />
              </div>
              <Button
                size="sm"
                onClick={handlePostUpdate}
                disabled={updatePosting || !updateTitle.trim() || !updateBody.trim()}
              >
                {updatePosting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Post Update
              </Button>
            </div>
          )}

          {/* Updates list */}
          {updates.length === 0 ? (
            <p className="text-sm text-muted-foreground">No updates posted yet.</p>
          ) : (
            <div className="space-y-4">
              {updates.map(u => (
                <div key={u.id} className="border-b border-border/30 pb-4 last:border-0 last:pb-0">
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <p className="text-sm font-semibold">{u.title}</p>
                    {u.created_at && (
                      <span className="text-xs text-muted-foreground shrink-0">
                        {new Date(u.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{u.body}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Campaign</DialogTitle>
            <DialogDescription>
              You can update the title, description, and end date. The funding goal cannot be changed once the campaign is active.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} rows={4} className="resize-none" />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" min={todayStr} value={editEndDate} onChange={e => setEditEndDate(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={editSaving}>
              {editSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirm */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently cancel the campaign and issue refunds to all backers. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Campaign</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleCancel}
            >
              Cancel Campaign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
